import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArrayControlProps,
  ControlElement,
  createDefaultValue,
  encode,
  getControlPath,
  Paths,
  RankedTester,
  Resolve,
  Test,
} from "@jsonforms/core";
import { DispatchCell, withJsonFormsArrayControlProps } from "@jsonforms/react";
import { sentenceCase } from "change-case";
import filter from "lodash/filter";
import fpfilter from "lodash/fp/filter";
import fpflow from "lodash/fp/flow";
import fpkeys from "lodash/fp/keys";
import fpmap from "lodash/fp/map";
import join from "lodash/join";
import { Check, Plus, X } from "lucide-react";
import React from "react";

const { or, isObjectArrayControl, isPrimitiveArrayControl, rankWith } = Test;

export const tableArrayControlTester: RankedTester = rankWith(
  4,
  or(isObjectArrayControl, isPrimitiveArrayControl),
);

class TableArrayControl extends React.Component<ArrayControlProps, any> {
  confirmDelete = (path: string, index: number) => {
    const p = path.substring(0, path.lastIndexOf("."));
    this.props.removeItems?.(p, [index])();
  };

  render() {
    const {
      addItem,
      schema,
      rootSchema,
      path,
      data,
      visible,
      errors,
      label,

      childErrors,

      enabled,
    } = this.props;

    const createControlElement = (key?: string): ControlElement => ({
      type: "Control",
      label: false,
      scope: schema.type === "object" ? `#/properties/${key}` : "#",
    });
    const isValid = errors.length === 0;

    return (
      <div hidden={!visible} className="mb-10">
        <header className="flex items-center justify-between">
          <Label>{label}</Label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  disabled={!enabled}
                  onClick={addItem(
                    path,
                    createDefaultValue(schema, rootSchema),
                  )}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Add new item</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </header>
        <div>{!isValid ? errors : ""}</div>

        <div className="grid gap-2 mt-2">
          {!data || !Array.isArray(data) || data.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No {label.toLowerCase()} yet
            </p>
          ) : (
            data.map((_child, index) => {
              const childPath = Paths.compose(path, `${index}`);
              const errorsPerEntry: any[] = filter(childErrors, (error) => {
                const errorPath = getControlPath(error);
                return errorPath.startsWith(childPath);
              });

              return (
                <div key={childPath} className="flex items-start gap-2 group">
                  {schema.properties ? (
                    <div className="flex-1 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2">
                      {fpflow(
                        fpkeys,
                        fpfilter(
                          (prop) => schema.properties?.[prop]?.type !== "array",
                        ),
                        fpmap((prop) => {
                          const childPropPath = Paths.compose(
                            childPath,
                            prop.toString(),
                          );
                          return (
                            <div
                              key={childPropPath}
                              className="flex items-center gap-2"
                            >
                              <div>
                                <DispatchCell
                                  schema={Resolve.schema(
                                    schema,
                                    `#/properties/${encode(prop)}`,
                                    rootSchema,
                                  )}
                                  uischema={createControlElement(encode(prop))}
                                  path={childPath + "." + prop}
                                />
                                {errorsPerEntry.length > 0 && (
                                  <span className="text-destructive mt-1">
                                    {join(
                                      errorsPerEntry.map((e) => e.message),
                                      " and ",
                                    )}
                                  </span>
                                )}
                              </div>
                              {errorsPerEntry.length === 0 && (
                                <Check className="text-lc-active h-4 w-4" />
                              )}
                            </div>
                          );
                        }),
                      )(schema.properties)}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center gap-2">
                      <div className="w-full">
                        <DispatchCell
                          schema={schema}
                          uischema={createControlElement()}
                          path={childPath}
                        />
                        {errorsPerEntry.length > 0 && (
                          <span className="text-destructive mt-1">
                            {sentenceCase(
                              join(
                                errorsPerEntry.map((e) => e.message),
                                " and ",
                              ),
                            )}
                          </span>
                        )}
                      </div>
                      {errorsPerEntry.length === 0 && (
                        <Check className="text-lc-active h-4 w-4" />
                      )}
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={!enabled}
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to delete this item?",
                        )
                      ) {
                        this.confirmDelete(childPath, index);
                      }
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }
}

export default withJsonFormsArrayControlProps(TableArrayControl);
